import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import {
  mdiAccount,
  mdiAccountPlus,
  mdiFileDocumentOutline,
  mdiGift,
  mdiImage,
  mdiLogin,
  mdiMagnify,
  mdiTag,
  mdiViewGridOutline,
  mdiWeb,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SmartlistType } from "@/src/api/MediaAPI.types";

import CategoriesComponentSettingsModal from "./CategoriesComponentSettingsModal";
import Disposition, { cloneDispositionState } from "./Disposition";
import OfferComponentSettingsModal from "./OfferComponentSettingsModal";
import ProjectAPI from "../../../../api/ProjectAPI";
import SmartlistAPI from "../../../../api/SmartlistAPI";
import Block from "../../../../components/block/Block";
import { DRAG_TYPE } from "../../../../constants/dragTypes";
import useSaveHeaderFooterComponentListMutation from "../../../../hooks/mutations/useSaveHeaderFooterComponentListMutation";
import { headerFooterComponentListApiToDispositionState } from "../../../../utils/components";

import type { JSX, Ref } from "react";

import type { DispositionState, ItemDisposition } from "./Disposition";
import type { HeaderComponent } from "../../../../api/ProjectAPI.types";

export interface HeaderDispositionRef {
  onSave: () => Promise<void>;
}

function HeaderDisposition(
  _: unknown,
  ref: Ref<HeaderDispositionRef>,
): JSX.Element {
  const { projectSlug } = useParams<{
    projectSlug: string;
  }>();
  const { t } = useTranslation("editor-platform");

  const saveComponentListMutation = useSaveHeaderFooterComponentListMutation();

  const queryClient = useQueryClient();

  const [isOpenCategoriesSettingsModal, setIsOpenCategoriesSettingsModal] =
    useState(false);
  const [isOpenOfferSettingsModal, setIsOpenOfferSettingsModal] =
    useState(false);

  const [dispositionState, setDispositionState] =
    React.useState<DispositionState>({
      available: [],
      left: [],
      center: [],
      right: [],
    });
  const [prevDispositionState, setPrevDispositionState] =
    React.useState<DispositionState>({
      available: [],
      left: [],
      center: [],
      right: [],
    });

  const getComponentsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponentList<HeaderComponent>,
    params: [
      {
        projectSlug,
        sectionList: ["header"],
      },
    ],
  });

  const { data: headerComponents } = useQuery(getComponentsQuery);

  const getSmartlistListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSmartlistList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const { data: smartlists } = useQuery(getSmartlistListQuery);

  const defaultSmartlistCategories = smartlists?.data.find(
    (smartlist) =>
      smartlist.type === SmartlistType.STANDARD ||
      smartlist.type === SmartlistType.SERIES,
  );

  const categoriesComponent = useMemo(
    () =>
      headerComponents?.data.find(
        (component) => component.type === "categories",
      ),
    [headerComponents],
  );

  const offerComponent = useMemo(
    () =>
      headerComponents?.data.find((component) => component.type === "offer"),
    [headerComponents],
  );

  const handleClickCategoriesSettings = useMemo(() => {
    if (!categoriesComponent) return undefined;
    return (): void => {
      setIsOpenCategoriesSettingsModal(true);
    };
  }, [categoriesComponent]);

  const handleClickOfferSettings = useMemo(() => {
    if (!offerComponent) return undefined;
    return (): void => {
      setIsOpenOfferSettingsModal(true);
    };
  }, [offerComponent]);

  const headerElementList: ItemDisposition[] = useMemo(
    () => [
      {
        id: "logo",
        label: t("logo"),
        icon: <Icon path={mdiImage} size={1} />,
        rank: 1,
      },
      {
        id: "page_list",
        label: t("pageList"),
        icon: <Icon path={mdiFileDocumentOutline} size={1} />,
        rank: 2,
      },
      {
        id: "categories",
        label: t("categories"),
        icon: <Icon path={mdiViewGridOutline} size={1} />,
        rank: 3,
        onClickSettings: handleClickCategoriesSettings,
        params: {
          show_my_medias: true,
          show_my_playlists: true,
          smartlist_uuid: defaultSmartlistCategories?.uuid ?? "",
        },
      },
      {
        id: "user",
        label: t("userPanel"),
        icon: <Icon path={mdiAccount} size={1} />,
        rank: 4,
      },
      {
        id: "search_bar",
        label: t("searchBar"),
        icon: <Icon path={mdiMagnify} size={1} />,
        rank: 5,
      },
      {
        id: "login",
        label: t("login"),
        icon: <Icon path={mdiLogin} size={1} />,
        rank: 6,
      },
      {
        id: "register",
        label: t("register"),
        icon: <Icon path={mdiAccountPlus} size={1} />,
        rank: 7,
      },
      {
        id: "gift",
        label: t("gift"),
        icon: <Icon path={mdiGift} size={1} />,
        rank: 8,
      },
      {
        id: "lang",
        label: t("languages"),
        icon: <Icon path={mdiWeb} size={1} />,
        rank: 9,
      },
      {
        id: "offer",
        label: t("changeOffer"),
        icon: <Icon path={mdiTag} size={1} />,
        rank: 10,
        onClickSettings: handleClickOfferSettings,
      },
    ],
    [
      handleClickCategoriesSettings,
      t,
      defaultSmartlistCategories,
      handleClickOfferSettings,
    ],
  );

  useImperativeHandle<HeaderDispositionRef, HeaderDispositionRef>(
    ref,
    () => ({
      onSave: async (): Promise<void> => {
        await saveComponentListMutation.mutateAsync({
          dispositionState,
          prevDispositionState,
          section: "header",
          onSuccess: (): void => {
            queryClient.invalidateQueries(getComponentsQuery.queryKey);
          },
        });
      },
    }),
    [
      dispositionState,
      getComponentsQuery.queryKey,
      prevDispositionState,
      queryClient,
      saveComponentListMutation,
    ],
  );

  useEffect(() => {
    if (headerComponents) {
      const newState = headerFooterComponentListApiToDispositionState(
        headerComponents.data,
        headerElementList,
      );
      setDispositionState(newState);
      setPrevDispositionState(cloneDispositionState(newState));
    }
  }, [headerComponents, headerElementList]);

  const handleCloseCategoriesSettingsModal = (): void => {
    setIsOpenCategoriesSettingsModal(false);
  };

  const handleCloseOfferSettingsModal = (): void => {
    setIsOpenOfferSettingsModal(false);
  };

  return (
    <Block title={t("header")}>
      <Disposition
        acceptItemType={DRAG_TYPE.HEADER_COMPONENT_ITEM}
        dispositionState={dispositionState}
        setDispositionState={setDispositionState}
        zoneList={["left", "right"]}
      />
      <CategoriesComponentSettingsModal
        component={categoriesComponent}
        isOpen={isOpenCategoriesSettingsModal}
        onClose={handleCloseCategoriesSettingsModal}
      />
      <OfferComponentSettingsModal
        component={offerComponent}
        isOpen={isOpenOfferSettingsModal}
        onClose={handleCloseOfferSettingsModal}
      />
    </Block>
  );
}

export default forwardRef(HeaderDisposition);
